import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Download, RefreshCw, LogOut, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { ambassador: "Ambassador", donate: "Donate", volunteer: "Volunteer" };

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("sif_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/registrations`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setRegs(data);
    } catch {
      sessionStorage.removeItem("sif_admin_token");
      setToken("");
      toast.error("Session expired. Please sign in again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("sif_admin_token", data.token);
      setToken(data.token);
    } catch {
      setError("Incorrect password. Please try again.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("sif_admin_token");
    setToken("");
    setRegs([]);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/registrations/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "settle-it-forward-registrations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-cream flex items-center justify-center px-6">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm bg-white border-2 border-navy p-8"
        >
          <img src="/logo.png" alt="Settle It Forward" className="h-12 w-auto mb-8" />
          <h1 className="font-display text-2xl text-navy uppercase mb-1">Submissions</h1>
          <p className="text-sm text-steel mb-6">Private area. Enter the admin password.</p>
          <label htmlFor="admin-password-input" className="block text-xs font-semibold tracking-[0.18em] text-steel mb-1">
            PASSWORD
          </label>
          <div className="relative mb-4">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
            <input
              id="admin-password-input"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full h-12 bg-white border border-navy rounded-none pl-10 pr-4 text-sm text-navy placeholder:text-navy/35 outline-none focus:border-2 focus:shadow-[4px_4px_0_0_#E63946]"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-brandred mb-4">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="w-full h-12 bg-navy text-cream font-display text-lg tracking-wider uppercase hover:bg-brandred transition-colors duration-200"
          >
            Sign In
          </button>
          <a href="/" className="block text-center text-xs text-steel mt-6 hover:text-brandred transition-colors">
            Back to site
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="bg-navy text-cream px-6 md:px-12 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src="/logo-light.png" alt="Settle It Forward" className="h-9 w-auto" />
          <span data-testid="registrations-count" className="font-display text-sm tracking-wider uppercase text-cream/70">
            {regs.length} registration{regs.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="refresh-button"
            onClick={() => load(token)}
            className="flex items-center gap-2 px-3 h-10 border border-cream/30 text-cream text-sm hover:bg-cream/10 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            data-testid="export-csv-button"
            onClick={exportCsv}
            className="flex items-center gap-2 px-3 h-10 bg-brandred text-cream text-sm font-medium hover:bg-cream hover:text-navy transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="flex items-center gap-2 px-3 h-10 border border-cream/30 text-cream text-sm hover:bg-cream/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-8">
        {regs.length === 0 ? (
          <div className="border-2 border-dashed border-navy/30 bg-white/50 p-16 text-center">
            <Users className="w-10 h-10 text-navy/30 mx-auto mb-4" />
            <p data-testid="empty-state-message" className="text-steel">No registrations yet. They'll appear here as people register their interest.</p>
          </div>
        ) : (
          <div className="bg-white border-2 border-navy overflow-x-auto">
            <table data-testid="registrations-table" className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b-2 border-navy text-left">
                  {["Date", "Name", "Business", "Position", "Mobile", "Email", "Involvement"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold tracking-[0.15em] text-steel uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {regs.map((r) => (
                  <tr key={r.id} data-testid={`registration-row-${r.id}`} className="border-b border-navy/10 hover:bg-cream/60 transition-colors">
                    <td className="px-4 py-3 text-steel whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3 font-medium text-navy whitespace-nowrap">{r.first_name} {r.last_name}</td>
                    <td className="px-4 py-3 text-navy">{r.business_name || "-"}</td>
                    <td className="px-4 py-3 text-navy">{r.position_title || "-"}</td>
                    <td className="px-4 py-3 text-navy whitespace-nowrap">{r.phone}</td>
                    <td className="px-4 py-3 text-navy">{r.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.involvements.map((i) => (
                          <span key={i} className="px-2 py-0.5 bg-brandred/10 text-brandred text-xs font-medium">
                            {LABELS[i] || i}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
